import { setLoading } from "./loading.slice";
import { popAlerts, pushAlerts } from "../alerts/alerts.slice";
import { checkSession, logout } from "../session/session.action";
import {
  getActionTypeErrorMessage,
  getErrorMessage,
} from "../../../utils/errorMessages";

const loadingMiddleware =
  ({ dispatch }) =>
  (next) =>
  async (action) => {
    next(action);
    const { type, meta } = action;
    let isRequest = false;
    if (meta && meta.requestId && meta.requestStatus) {
      isRequest = true;
    }
    if (isRequest) {
      if (type.includes("/pending")) {
        dispatch(setLoading({ id: type.replace("/pending", ""), state: true }));
      } else if (type.includes("/fulfilled")) {
        dispatch(
          setLoading({ id: type.replace("/fulfilled", ""), state: false })
        );
      } else if (type.includes("/rejected")) {
        // ── ONE ALERT OWNER PER THUNK ──────────────────────────────────────────────────
        //
        // This generic alert is the DEFAULT owner: it speaks for every rejected thunk
        // that nothing else reports. A module middleware that alerts a rejection itself
        // must therefore opt its action type out below — otherwise one failure produces
        // TWO messages, on two different surfaces (this one pushes to state.alerts,
        // rendered by <Alerts/> in DashboardLayout; module middlewares dispatch addAlert,
        // rendered by the <Notification/> snackbar), and the generic wording paints over
        // the server's actionable reason (the duplicate-module-set message naming the
        // colliding bundle is the one that keeps getting lost).
        //
        // The invariant, and it runs both ways:
        //   * a thunk WITH a dedicated alert  -> its type MUST appear in this list;
        //   * a thunk WITHOUT one             -> it must NOT appear, this is its only
        //                                        alert and removing it means silence.
        // Deciding either half means reading the module middleware and this list
        // together. Checking only one of them is how three passes of whack-a-mole went.
        //
        // Same-event exception for BOTH owners: a dispatch the admin never asked for,
        // whose failure says nothing about what they did ask for — the cache warm-up
        // inside resolveBundleId, the confirming re-read after a save that has already
        // succeeded, the list refresh behind a dialog that has already closed — marks
        // itself `suppressErrorAlert` and neither owner speaks. A completed save must
        // never be reported as failed.
        const ignoreRejectActions = [
          checkSession.rejected.type,
          logout.rejected.type,
          // companies.mdl.js — alerts the server's own message via action.payload.
          "companies/updateAssign/rejected",
          "companies/getCompanies/rejected",
          "companies/setCompanyBundle/rejected",
          "bundle/getBundleOptions/rejected",
          // bundles.middleware.js — all five alert action.payload, which for these
          // endpoints is the server's reason ("A bundle with the same modules already
          // exists: ...", "Bundle name already exists").
          "bundle/create/rejected",
          "bundle/update/rejected",
          "bundle/setStatus/rejected",
          "bundle/delete/rejected",
          "bundle/getStructure/rejected",
          // approval.mdl.js — alerts the server's refusal reason.
          "approvals/putApproval/rejected",
        ];
        // NOT listed, deliberately: companies/getCompanyFeature and
        // bundle/getModuleListing have no dedicated alert, so this generic one is their
        // owner and has to stay.
        const isAlertSuppressed = Boolean(
          action.meta && action.meta.arg && action.meta.arg.suppressErrorAlert
        );
        if (!ignoreRejectActions.includes(type) && !isAlertSuppressed) {
          const currentTimestamps = new Date().getTime();

          let errorMessage = getActionTypeErrorMessage(type);

          if (action.payload) {
            const specificErrorMessage = getErrorMessage(action.payload);
            if (
              specificErrorMessage &&
              specificErrorMessage !==
                "An unexpected error occurred. Please try again."
            ) {
              errorMessage = specificErrorMessage;
            }
          }

          // Set Alert Message
          const alertMessage = {
            message: errorMessage,
            timestamps: currentTimestamps,
          };
          dispatch(pushAlerts(alertMessage));
          setTimeout(() => {
            dispatch(popAlerts(alertMessage));
          }, 3000);
        }
        dispatch(
          setLoading({ id: type.replace("/rejected", ""), state: false })
        );
      }
    }
  };

const middlewares = [loadingMiddleware];

export default middlewares;
